import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS } from '../../constants/permissions';
import agentService from '../../services/agentService';
import webhookService from '../../services/webhookService';
import PageLoader from '../shared/PageLoader';
import ShareAgentDialog from './ShareAgentDialog';
import ManageSharingDialog from '../shared/ManageSharingDialog';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Snackbar,
  Chip,
} from '@mui/material';
import {
  SmartToy,
  Add,
  Edit,
  Delete,
  Memory,
  MenuBook,
  Link,
  Code,
  DataObject,
  Science,
  ContentCopy,
  Share,
  Person,
  PlayCircle,
  CheckCircle,
  Groups,
  LockOutlined,
  ManageAccounts,
} from '@mui/icons-material';
import {
  LayoutGrid,
  List as ListIconLucide,
  Plus,
  Play,
  ExternalLink,
  MoreVertical,
  Activity,
  ShieldCheck,
  Tag,
  Search,
} from 'lucide-react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import styles from './Agents.module.scss';

// Helper component for detailed tooltips (similar to KnowledgeBaseDialog)
const InfoTooltip = ({ title, content, children, placement = "top", maxWidth = 1500 }) => (
  <Tooltip
    title={
      <Box sx={{ 
        width: '100%',
        maxHeight: '400px',
        overflowY: 'auto',
        p: 1,
        boxSizing: "border-box",
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: '4px',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.5)',
          },
        },
      }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 1, color: "white" }}
        >
          {title}
        </Typography>
        {typeof content === "string" ? (
          <Typography variant="body2" sx={{ 
            color: "rgba(255,255,255,0.9)",
            wordWrap: "break-word",
            overflowWrap: "break-word"
          }}>
            {content}
          </Typography>
        ) : (
          content
        )}
      </Box>
    }
    placement={placement}
    arrow
    enterDelay={300}
    leaveDelay={200}
    sx={{
      "& .MuiTooltip-tooltip": {
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        fontSize: "0.875rem",
        maxWidth: `${maxWidth}px !important`,
        padding: "12px 16px",
        wordWrap: "break-word",
        overflowWrap: "break-word",
        whiteSpace: "normal",
        boxSizing: "border-box",
      },
      "& .MuiTooltip-arrow": {
        color: "rgba(0, 0, 0, 0.9)",
      },
    }}
  >
    {children}
  </Tooltip>
);

/** Dashboard filter: matches labels in UI (classification is derived from agent data). */
const AGENT_FILTER_ALL = 'All';

const AGENT_FILTER_OPTIONS = [
  { value: AGENT_FILTER_ALL, label: 'All Agents' },
  { value: 'Generative Agent', label: 'Generative Agent' },
  { value: 'API Agent', label: 'API Agent' },
  { value: 'Conversational Agent', label: 'Conversational Agent' },
];

function getAgentCategory(agent) {
  const explicit =
    agent?.agent_type ??
    agent?.agentType ??
    agent?.category ??
    agent?.type;
  if (explicit != null && String(explicit).trim() !== '') {
    const t = String(explicit).toLowerCase();
    if (t.includes('api')) return 'API Agent';
    if (t.includes('conversational') || t.includes('conversation')) {
      return 'Conversational Agent';
    }
    if (t.includes('generative')) return 'Generative Agent';
  }
  const hasTools = getAgentToolsCount(agent) > 0;
  const hasMemory =
    agent?.memory?.enabled === true ||
    agent?.features?.memory === true;
  if (hasTools) return 'API Agent';
  if (hasMemory) return 'Conversational Agent';
  return 'Generative Agent';
}

function getAgentToolsCount(agent) {
  if (typeof agent?.tools_count === 'number') return agent.tools_count;
  if (Array.isArray(agent?.tools)) return agent.tools.length;
  return 0;
}

function getAgentChainingCount(agent) {
  if (typeof agent?.agent_chaining_count === 'number') return agent.agent_chaining_count;
  return agent?.agent_chaining?.selected_agents?.length || 0;
}

function getKnowledgeBaseCount(agent) {
  if (typeof agent?.knowledge_base_count === 'number') return agent.knowledge_base_count;
  return agent?.knowledge_base?.knowledge_bases?.length || 0;
}

function getKnowledgeBaseD3Count(agent) {
  if (typeof agent?.knowledge_base_dim3_count === 'number') return agent.knowledge_base_dim3_count;
  return agent?.knowledge_base_dim3?.knowledge_bases?.length || 0;
}

const Agents = () => {
  const { isDarkMode } = useTheme();
  const { has, ready: permissionsReady } = usePermissions();
  const canShareResources = permissionsReady && has(PERMISSIONS.RESOURCE_SHARING);
  const navigate = useNavigate();

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#e36250 !important',
      },
      secondary: {
        main: '#8b5cf6',
      },
    },
  });

  // State management
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const initialLoadingRef = useRef(true);
  const requestIdRef = useRef(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [deletingAgent, setDeletingAgent] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [agentToDuplicate, setAgentToDuplicate] = useState(null);
  const [duplicatingAgent, setDuplicatingAgent] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [agentToShare, setAgentToShare] = useState(null);
  const [shareSuccess, setShareSuccess] = useState('');
  const [shareSeverity, setShareSeverity] = useState('success');
  const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
  const [manageSharingOpen, setManageSharingOpen] = useState(false);
  const [agentToManage, setAgentToManage] = useState(null);

  // Webhook and agent status tracking
  const [agentRunningStates, setAgentRunningStates] = useState(new Map());
  const [agentExecutionModes, setAgentExecutionModes] = useState({});
  const executionModeCacheRef = useRef(new Map());

  // View mode state with localStorage persistence
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('agentsViewMode') || 'list';
  });

  // Infinite scroll state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalAgents, setTotalAgents] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 400);
  const debouncedSearchQueryRef = useRef(debouncedSearchQuery);
  debouncedSearchQueryRef.current = debouncedSearchQuery;

  const [selectedFilter, setSelectedFilter] = useState(AGENT_FILTER_ALL);

  const isFilterActive = Boolean(
    debouncedSearchQuery.trim() ||
    selectedFilter !== AGENT_FILTER_ALL
  );

  const filteredAgents = useMemo(() => {
    return agents.filter((agent) => {
      const category = getAgentCategory(agent);
      const matchesType =
        selectedFilter === AGENT_FILTER_ALL || category === selectedFilter;
      return matchesType;
    });
  }, [agents, selectedFilter]);

  // Owned and shared agents render as separate sections (shared entries carry
  // the __shared flag from the list response's `shared` array).
  const ownFilteredAgents = useMemo(
    () => filteredAgents.filter((agent) => !agent.__shared),
    [filteredAgents]
  );
  const sharedFilteredAgents = useMemo(
    () => filteredAgents.filter((agent) => agent.__shared),
    [filteredAgents]
  );

  const loadAgents = useCallback(async (pageNum = 1, append = false, query = '') => {
    const currentRequestId = ++requestIdRef.current;

    if (append) {
      setLoadingMore(true);
    } else if (initialLoadingRef.current) {
      setLoading(true);
    } else {
      setIsSearching(true);
    }
    
    try {
      const trimmedQuery = typeof query === 'string' && query.trim() ? query.trim() : null;
      const response = await agentService.getAgents(pageNum, pageSize, trimmedQuery);

      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      const ownedAgents = response.agents || [];
      const rawShared = pageNum === 1 && Array.isArray(response.shared) ? response.shared : [];
      const sharedAgents = rawShared
        .map((item) => ({
          ...(item.resource || {}),
          id: item.resource_id ?? item.resource?.id,
          __shared: true,
          __sharePermission: item.permission || 'read',
          __shareId: item.share_id,
          __shareOwner: item.owner,
          __shareOptions: item.options,
        }))
        .filter((agent) => {
          if (!trimmedQuery) return true;
          return (agent.name || '').toLowerCase().includes(trimmedQuery.toLowerCase());
        });
      const agentsData = [...ownedAgents, ...sharedAgents];
      console.log('Loaded agents data:', agentsData);
      console.log('Pagination info:', { page: response.page, size: response.size, total: response.total, total_pages: response.total_pages });

      if (append) {
        setAgents(prev => [...prev, ...agentsData]);
      } else {
        setAgents(agentsData);
      }
      
      setTotalPages(response.total_pages || 0);
      if (pageNum === 1) {
        setTotalAgents((response.total || 0) + sharedAgents.length);
      }
      setHasMore(pageNum < (response.total_pages || 0));
    } catch (err) {
      if (currentRequestId === requestIdRef.current) {
        setError('Failed to load agents');
        console.error('Error loading agents:', err);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
        setIsSearching(false);
        if (initialLoadingRef.current) {
          initialLoadingRef.current = false;
          setInitialLoading(false);
        }
      }
    }
  }, [pageSize]);

  // Load initial agents on component mount and whenever debouncedSearchQuery changes
  const isInitialMountRef = useRef(true);
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      loadAgents(1, false, '');
      return;
    }

    setPage(1);
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
    loadAgents(1, false, debouncedSearchQuery);
  }, [debouncedSearchQuery, loadAgents]);

  // Listen for webhook events to update agent running states
  useEffect(() => {
    const handleAgentStarted = (webhookData) => {
      if (webhookData?.agent_id) {
        setAgentRunningStates(prev => new Map(prev).set(webhookData.agent_id, 'running'));
      }
    };

    const handleAgentCompleted = (webhookData) => {
      if (webhookData?.agent_id) {
        setAgentRunningStates(prev => {
          const updated = new Map(prev);
          updated.delete(webhookData.agent_id);
          return updated;
        });
      }
    };

    const handleAgentFailed = (webhookData) => {
      if (webhookData?.agent_id) {
        setAgentRunningStates(prev => new Map(prev).set(webhookData.agent_id, 'failed'));
        // Auto-clear failed state after 5 seconds
        setTimeout(() => {
          setAgentRunningStates(prev => {
            const updated = new Map(prev);
            if (updated.get(webhookData.agent_id) === 'failed') {
              updated.delete(webhookData.agent_id);
            }
            return updated;
          });
        }, 5000);
      }
    };

    const unsubscribeStarted = webhookService.subscribe('agent_started', handleAgentStarted);
    const unsubscribeCompleted = webhookService.subscribe('agent_completed', handleAgentCompleted);
    const unsubscribeFailed = webhookService.subscribe('agent_failed', handleAgentFailed);

    return () => {
      unsubscribeStarted();
      unsubscribeCompleted();
      unsubscribeFailed();
    };
  }, []);

  // Infinite scroll handler
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let isFetching = false;

    const handleScroll = () => {
      // Get scroll position from the container
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      
      // Check if user has scrolled near the bottom (within 300px)
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 300;
      
      console.log('Scroll check:', {
        scrollTop,
        scrollHeight,
        clientHeight,
        isNearBottom,
        hasMore,
        loadingMore,
        loading,
        isFetching,
        page,
        totalPages
      });
      
      if (isNearBottom && hasMore && !loadingMore && !loading && !isSearching && !isFetching) {
        isFetching = true;
        const nextPage = page + 1;
        console.log('Loading more agents - Page:', nextPage, 'HasMore:', hasMore, 'TotalPages:', totalPages);
        setPage(nextPage);
        loadAgents(nextPage, true, debouncedSearchQueryRef.current).finally(() => {
          isFetching = false;
        });
      }
    };

    // Throttle scroll events for better performance
    let scrollTimeout;
    const throttledHandleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(handleScroll, 100);
    };

    container.addEventListener('scroll', throttledHandleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', throttledHandleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [hasMore, loadingMore, loading, isSearching, page, totalPages, loadAgents]);

  const handleCreateAgent = () => {
    navigate('/agent-create');
  };

  const handleMenuClick = (event, agent) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedAgent(agent);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAgent(null);
  };

  const handleEditAgent = () => {
    if (selectedAgent) {
      openAgent(selectedAgent);
    }
    handleMenuClose();
  };

  const handleTestAgent = () => {
    if (selectedAgent) {
      navigate(`/agent-test?id=${selectedAgent.id}`);
    }
    handleMenuClose();
  };

  // Shared agents open the editor with their permission in the URL: write
  // shares get the shared-edit flow, read shares open it as a locked view
  // (AgentCreate re-verifies against the server either way).
  const openAgent = (agent) => {
    if (agent?.__shared) {
      const permission = agent.__sharePermission === 'write' ? 'write' : 'read';
      navigate(`/agent-create?id=${agent.id}&shared=true&permission=${permission}`);
    } else {
      navigate(`/agent-create?id=${agent.id}`);
    }
  };

  const handleDeleteAgent = () => {
    if (selectedAgent) {
      setAgentToDelete(selectedAgent);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!agentToDelete) return;

    setDeletingAgent(true);
    try {
      await agentService.deleteAgent(agentToDelete.id);
      // Reset to page 1 and reload all agents
      setPage(1);
      setAgents([]);
      await loadAgents(1, false, debouncedSearchQueryRef.current);
      setDeleteDialogOpen(false);
      setAgentToDelete(null);
      setSuccess('Agent deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete agent');
      console.error('Error deleting agent:', err);
    } finally {
      setDeletingAgent(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setAgentToDelete(null);
  };

  const handleDuplicateAgent = () => {
    if (selectedAgent) {
      setAgentToDuplicate(selectedAgent);
      setDuplicateDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleConfirmDuplicate = async () => {
    if (!agentToDuplicate) return;

    setDuplicatingAgent(true);
    try {
      const fullAgent = await agentService.getAgent(agentToDuplicate.id);
      // Create a copy of the agent data with modified name
      const duplicatedAgentData = {
        ...fullAgent,
        name: `Copy - ${agentToDuplicate.name}`,
        is_publish: false,
        // Remove id and timestamps as they should be generated by the server
        id: undefined,
        created_at: undefined,
        updated_at: undefined,
      };

      // Create the new agent
      await agentService.createAgent(duplicatedAgentData);

      // Reset to page 1 and reload all agents
      setPage(1);
      setAgents([]);
      await loadAgents(1, false, debouncedSearchQueryRef.current);

      setDuplicateDialogOpen(false);
      setAgentToDuplicate(null);
      setSuccess('Agent duplicated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to duplicate agent');
      console.error('Error duplicating agent:', err);
    } finally {
      setDuplicatingAgent(false);
    }
  };

  const handleCancelDuplicate = () => {
    setDuplicateDialogOpen(false);
    setAgentToDuplicate(null);
  };

  const handleShare = () => {
    if (selectedAgent) {
      setAgentToShare(selectedAgent);
    }
    setShareDialogOpen(true);
    handleMenuClose();
  };

  const handleManageSharing = () => {
    if (selectedAgent) {
      setAgentToManage(selectedAgent);
      setManageSharingOpen(true);
    }
    handleMenuClose();
  };

  const handleCloseShareSnackbar = () => {
    setShareSnackbarOpen(false);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setAgentToShare(null);
  };

  const handleShareSuccess = (result) => {
    const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
    setShareSuccess(warnings.length ? `Agent shared. ${warnings.join(' ')}` : 'Agent shared successfully.');
    setShareSeverity(warnings.length ? 'warning' : 'success');
    setShareSnackbarOpen(true);
    setShareDialogOpen(false);
    setAgentToShare(null);
  };




  // Handle view mode change with localStorage persistence
  const handleViewModeChange = (newView) => {
    if (newView) {
      setViewMode(newView);
      localStorage.setItem('agentsViewMode', newView);
    }
  };

  const getStatusStudioTone = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'inactive') return styles.statusToneError;
    if (normalized === 'draft') return styles.statusToneWarning;
    if (normalized === 'active') return styles.statusToneHealthy;
    return styles.statusToneNeutral;
  };

  const getAgentStatus = (agent) => {
    // Check if agent is currently running (from webhook)
    const runningState = agentRunningStates.get(agent.id);
    if (runningState) {
      return runningState === 'running' ? 'Running' : 'Failed';
    }

    // Check if agent has a status field
    if (agent.status) {
      return agent.status;
    }
    
    // Check if agent has other status-related fields
    if (agent.is_active !== undefined) {
      return agent.is_active ? 'Active' : 'Inactive';
    }
    
    if (agent.state) {
      return agent.state;
    }
    
    // Default to Active if no status information is available
    return 'Active';
  };

  const getLocalExecutionMode = useCallback((agent) => {
    if (agent.agent_team || agent.team || agent.features?.agent_team) return 'Team';
    if (agent.features?.agent_chaining && getAgentChainingCount(agent) > 0) return 'Workflow';
    return 'Single';
  }, []);

  const getAgentExecutionMode = (agent) =>
    agentExecutionModes[agent.id] || executionModeCacheRef.current.get(agent.id) || getLocalExecutionMode(agent);

  useEffect(() => {
    const updates = {};
    agents.forEach((agent) => {
      const mode = getLocalExecutionMode(agent);
      executionModeCacheRef.current.set(agent.id, mode);
      updates[agent.id] = mode;
    });
    setAgentExecutionModes((prev) => ({ ...prev, ...updates }));
  }, [agents, getLocalExecutionMode]);

  const getStatusColor = (status) => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case 'active':
        return '#4caf50';
      case 'inactive':
        return '#f44336';
      case 'draft':
        return '#ff9800';
      case 'running':
        return '#2196f3';
      case 'completed':
        return '#4caf50';
      case 'failed':
        return '#f44336';
      default:
        // Default to active status color if no status is provided
        return '#4caf50';
    }
  };


  if (initialLoading) {
    return (
      <ThemeProvider theme={muiTheme}>
        <PageLoader message="Loading agents..." />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Box ref={containerRef} className={styles.container}>
        <div className={styles.studioInner}>
          <header className={styles.stickyHeader}>
            <div className={styles.headerTop}>
              <div className={styles.titleBlock}>
                <div className={styles.titleRow}>
                  <h1 className={styles.pageTitle}>Agents</h1>
                  <span className={styles.pageTitleMeta}>
                    {totalAgents} Total
                  </span>
                </div>
                <div className={styles.statPills}>
                  <span className={styles.statPill}>
                    <span className={styles.pillDot} />
                    {totalAgents} {totalAgents === 1 ? 'Agent' : 'Agents'}
                  </span>
                  <span className={styles.statPill}>
                    <Activity size={12} strokeWidth={2.5} color="var(--primary)" />
                    {filteredAgents.length} shown
                  </span>
                  <span className={styles.statPill}>
                    <ShieldCheck size={12} strokeWidth={2.5} color="#22c55e" />
                    {totalPages > 0
                      ? `${totalPages} page${totalPages !== 1 ? 's' : ''}`
                      : '—'}
                  </span>
                </div>
              </div>
              <div className={styles.headerActions}>
                <div className={styles.viewSwitch} role="group" aria-label="View mode">
                  <Tooltip title="Card view" arrow>
                    <button
                      type="button"
                      className={`${styles.viewSwitchBtn} ${viewMode === 'card' ? styles.viewSwitchBtnActive : ''}`}
                      onClick={() => handleViewModeChange('card')}
                      aria-pressed={viewMode === 'card'}
                    >
                      <LayoutGrid size={18} strokeWidth={2} />
                    </button>
                  </Tooltip>
                  <Tooltip title="List view" arrow>
                    <button
                      type="button"
                      className={`${styles.viewSwitchBtn} ${viewMode === 'list' ? styles.viewSwitchBtnActive : ''}`}
                      onClick={() => handleViewModeChange('list')}
                      aria-pressed={viewMode === 'list'}
                    >
                      <ListIconLucide size={18} strokeWidth={2} />
                    </button>
                  </Tooltip>
                </div>
                <button
                  type="button"
                  className={styles.createAgentBtn}
                  onClick={handleCreateAgent}
                >
                  <Plus size={18} strokeWidth={2.5} />
                  Create Agent
                </button>
              </div>
            </div>
            <div className={styles.filterRow}>
              <div className={styles.searchWrap}>
                {isSearching ? (
                  <div className={styles.searchSpinner} aria-hidden>
                    <CircularProgress size={16} sx={{ color: 'var(--primary)' }} />
                  </div>
                ) : (
                  <Search
                    className={styles.searchIcon}
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                )}
                <input
                  type="search"
                  className={styles.searchInput}
                  placeholder="Search agents by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search agents by name"
                />
              </div>
              <div
                className={styles.filterChips}
                role="tablist"
                aria-label="Filter by agent type"
              >
                {AGENT_FILTER_OPTIONS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={selectedFilter === value}
                    className={`${styles.filterChip} ${selectedFilter === value ? styles.filterChipActive : ''}`}
                    onClick={() => setSelectedFilter(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </header>

        {/* Success/Error Messages */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Agents Grid/List */}
        <Box className={`${styles.content} ${styles.contentSection}`}>
          {agents.length === 0 && !isFilterActive ? (
            <Box className={styles.emptyState}>
              <SmartToy className={styles.emptyIcon} />
              <Typography variant="h6" className={styles.emptyTitle}>
                No agents created yet
              </Typography>
              <Typography variant="body2" className={styles.emptyDescription}>
                Create your first AI agent to get started
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateAgent}
                className={styles.emptyCreateButton}
              >
                Create Your First Agent
              </Button>
            </Box>
          ) : (
            <>
              {filteredAgents.length === 0 ? (
                <Box className={styles.emptyFilterState}>
                  <Search className={styles.emptyFilterIcon} size={48} strokeWidth={1.5} />
                  <Typography variant="h6" className={styles.emptyFilterTitle}>
                    No agents match your search or filter
                  </Typography>
                  <Typography variant="body2" className={styles.emptyFilterDesc}>
                    Try different keywords or select All Agents to see everyone loaded so far.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedFilter(AGENT_FILTER_ALL);
                    }}
                    sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                  >
                    Clear search and filters
                  </Button>
                </Box>
              ) : (
              <>
              {/* Card View */}
              {viewMode === 'card' && (() => {
                const renderAgentCard = (agent) => {
                  const agentStatus = getAgentStatus(agent);
                  const executionMode = getAgentExecutionMode(agent);

                  return (
                    <Box
                      className={styles.agentCard}
                      key={agent.id}
                      onClick={() => openAgent(agent)}
                    >
                      <Box className={styles.cardContent}>
                        <Box className={styles.cardTopBar}>
                          <div className={styles.cardIconWrap}>
                            <SmartToy sx={{ fontSize: 18 }} />
                          </div>
                          <IconButton
                            size="small"
                            disableRipple
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuClick(e, agent);
                            }}
                            className={styles.menuButton}
                          >
                            <MoreVertical size={16} strokeWidth={2} />
                          </IconButton>
                        </Box>
                        <Box
                          className={`${styles.statusBadge} ${getStatusStudioTone(agentStatus)}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {agentStatus}
                        </Box>
                              <InfoTooltip
                                title={agent.name}
                                content={
                                  <Box>
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                                        🧠 Model Configuration
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                        <strong>Provider:</strong> {agent.llm_provider?.name || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                        <strong>Model:</strong> {agent.llm_model?.name || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                        <strong>Temperature:</strong> {agent.temperature || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                        <strong>Top P:</strong> {agent.top_p || 'N/A'}
                                      </Typography>
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                                        ⚙️ Agent Configuration
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                        <strong>Role:</strong> {agent.agent_role || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                        <strong>Goal:</strong> {agent.agent_goal || 'N/A'}
                                      </Typography>
                                      {agent.variables && (
                                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                          <strong>Variables:</strong> {agent.variables}
                                        </Typography>
                                      )}
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                                        🧠 Memory & Context
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                        <strong>Memory:</strong> {agent.memory?.enabled ? 'Enabled' : 'Disabled'}
                                      </Typography>
                                      {agent.memory?.enabled && (
                                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                          <strong>Max Messages:</strong> {agent.memory?.max_messages || 'N/A'}
                                        </Typography>
                                      )}
                                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                        <strong>Rules book:</strong> {agent.features?.knowledge_base ? 'Enabled' : 'Disabled'}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                        <strong>Customer Data:</strong> {agent.features?.knowledge_base_d3 ? 'Enabled' : 'Disabled'}
                                      </Typography>
                                    </Box>

                                    {agent.features?.agent_chaining && getAgentChainingCount(agent) > 0 && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1, color: "#9c27b0", fontWeight: 600 }}>
                                          🔗 Agent Chaining
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                          <strong>Strategy:</strong> {agent.agent_chaining?.chaining_strategy || 'N/A'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                          <strong>Chained Agents:</strong> {getAgentChainingCount(agent)}
                                        </Typography>
                                        {agent.agent_chaining?.selected_agents?.length > 0 && (
                                          <>
                                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                              <strong>Agent Names:</strong>
                                            </Typography>
                                            {agent.agent_chaining.selected_agents.map((chainedAgent, index) => (
                                              <Typography key={index} variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", ml: 1 }}>
                                                • {chainedAgent.name || chainedAgent.agent_name || `Agent ${index + 1}`}
                                              </Typography>
                                            ))}
                                          </>
                                        )}
                                      </Box>
                                    )}

                                    {getAgentToolsCount(agent) > 0 && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1, color: "#e36250", fontWeight: 600 }}>
                                          🛠️ Tools & Integrations
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                          <strong>Total Tools:</strong> {getAgentToolsCount(agent)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                          <strong>Tool details:</strong> Open the agent to view configured tools.
                                        </Typography>
                                        {Array.isArray(agent.tools) && agent.tools.map((tool, index) => (
                                          <Typography key={index} variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", ml: 1 }}>
                                            • {tool.app_name} - {tool.action_name}
                                          </Typography>
                                        ))}
                                      </Box>
                                    )}

                                    {agent.features?.knowledge_base && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                                          📚 Rule book
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                          <strong>Status:</strong> {agent.features.knowledge_base ? 'Enabled' : 'Disabled'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                          <strong>RAG Type:</strong> {agent.knowledge_base?.rag_type || 'N/A'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                          <strong>Rules books:</strong> {getKnowledgeBaseCount(agent)}
                                        </Typography>
                                        {agent.knowledge_base?.knowledge_bases?.length > 0 && (
                                          <>
                                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                              <strong>Configuration:</strong>
                                            </Typography>
                                            {agent.knowledge_base.knowledge_bases.map((kb, index) => (
                                              <Typography key={index} variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", ml: 1 }}>
                                                • Chunks: {kb.numberOfChunks}, Threshold: {kb.scoreThreshold}, Type: {kb.retrievalType}
                                              </Typography>
                                            ))}
                                          </>
                                        )}
                                      </Box>
                                    )}

                                    {agent.features?.knowledge_base_d3 && (
                                      <Box>
                                        <Typography variant="body2" sx={{ mb: 1, color: "#9c27b0", fontWeight: 600 }}>
                                          📊 Customer Data
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                          <strong>Status:</strong> {agent.features.knowledge_base_d3 ? 'Enabled' : 'Disabled'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                          <strong>RAG Type:</strong> {agent.knowledge_base_dim3?.rag_type || 'N/A'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                          <strong>Customer Data Sources:</strong> {getKnowledgeBaseD3Count(agent)}
                                        </Typography>
                                        {agent.knowledge_base_dim3?.knowledge_bases?.length > 0 && (
                                          <>
                                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                              <strong>Configuration:</strong>
                                            </Typography>
                                            {agent.knowledge_base_dim3.knowledge_bases.map((kb, index) => (
                                              <Typography key={index} variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", ml: 1 }}>
                                                • Chunks: {kb.numberOfChunks}, Threshold: {kb.scoreThreshold}, Type: {kb.retrievalType}
                                              </Typography>
                                            ))}
                                          </>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                }
                                maxWidth={500}
                                placement="top"
                              >
                                <Typography component="h3" variant="h6" className={styles.cardName}>
                                  {agent.name}
                                </Typography>
                              </InfoTooltip>

                        <Box
                          className={styles.typeBadge}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {executionMode === 'Team' ? (
                            <Groups sx={{ fontSize: 12 }} />
                          ) : executionMode === 'Workflow' ? (
                            <Link sx={{ fontSize: 12 }} />
                          ) : (
                            <Tag size={8} strokeWidth={2.5} />
                          )}
                          {executionMode}
                        </Box>

                        {/* Agent Description with Tooltip */}
                        <Tooltip title={agent.description || 'No description provided'} arrow>
                          <Typography
                            variant="body2"
                            className={styles.cardDesc}
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              cursor: 'pointer',
                            }}
                          >
                            {agent.description || 'No description provided'}
                          </Typography>
                        </Tooltip>

                        {/* Additional Info */}
                        <Box className={styles.metaChips}>
                          {agent.__shared && (
                            <Chip
                              size="small"
                              icon={agent.__sharePermission === 'read' ? <LockOutlined /> : undefined}
                              label={`Shared by ${agent.__shareOwner?.name || agent.__shareOwner?.email || 'teammate'}`}
                              color={agent.__sharePermission === 'write' ? 'success' : 'default'}
                            />
                          )}
                          {agent.is_publish === true && (
                            <Tooltip title="Published" arrow>
                              <Box className={styles.infoItem}>
                                <CheckCircle className={styles.infoIcon} />
                                <Typography variant="caption" className={styles.infoText}>
                                  Published
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                          {agent.variables && (
                            <Tooltip title={`Variables: ${agent.variables}`} arrow>
                              <Box className={styles.infoItem}>
                                <DataObject className={styles.infoIcon} />
                                <Typography variant="caption" className={styles.infoText}>
                                  Variables
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                          {agent.features?.agent_chaining && getAgentChainingCount(agent) > 0 && (
                            <InfoTooltip
                              title={`Chained Agents (${getAgentChainingCount(agent)})`}
                              content={
                                <Box>
                                  {(!agent.agent_chaining?.selected_agents || agent.agent_chaining.selected_agents.length === 0) && (
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                      {getAgentChainingCount(agent)} chained agent{getAgentChainingCount(agent) !== 1 ? 's' : ''}
                                    </Typography>
                                  )}
                                  {(agent.agent_chaining?.selected_agents || []).map((chainedAgent, index) => (
                                    <Typography key={index} variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                      • {chainedAgent.name || chainedAgent.agent_name || `Agent ${index + 1}`}
                                    </Typography>
                                  ))}
                                </Box>
                              }
                              maxWidth={300}
                              placement="top"
                            >
                              <Box className={styles.infoItem}>
                                <Link className={styles.infoIcon} />
                                <Typography variant="caption" className={styles.infoText}>
                                  Chained
                                </Typography>
                              </Box>
                            </InfoTooltip>
                          )}
                          {executionMode === 'Team' && (
                            <InfoTooltip
                              title="Agent Team"
                              content="This agent runs as a leader-led team. Team configuration takes precedence over workflow chaining."
                              maxWidth={300}
                              placement="top"
                            >
                              <Box className={styles.infoItem}>
                                <Groups className={styles.infoIcon} />
                                <Typography variant="caption" className={styles.infoText}>
                                  Team
                                </Typography>
                              </Box>
                            </InfoTooltip>
                          )}
                          {getAgentToolsCount(agent) > 0 && (
                            <InfoTooltip
                              title={`Tools (${getAgentToolsCount(agent)})`}
                              content={
                                <Box>
                                  {(!agent.tools || agent.tools.length === 0) && (
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                      {getAgentToolsCount(agent)} configured tool{getAgentToolsCount(agent) !== 1 ? 's' : ''}
                                    </Typography>
                                  )}
                                  {(agent.tools || []).slice(0, 5).map((tool, index) => (
                                    <Typography key={index} variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                      • {tool.app_name} - {tool.action_name}
                                    </Typography>
                                  ))}
                                  {Array.isArray(agent.tools) && agent.tools.length > 5 && (
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", fontStyle: 'italic' }}>
                                      ... and {agent.tools.length - 5} more
                                    </Typography>
                                  )}
                                </Box>
                              }
                              maxWidth={400}
                              placement="top"
                            >
                              <Box className={styles.infoItem}>
                                <Code className={styles.infoIcon} />
                                <Typography variant="caption" className={styles.infoText}>
                                  Tools
                                </Typography>
                              </Box>
                            </InfoTooltip>
                          )}
                          {agent.features?.knowledge_base && (
                            <InfoTooltip
                              title="Rule book"
                              content={
                                <Box>
                                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                    Status: {agent.features.knowledge_base ? 'Enabled' : 'Disabled'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                    RAG Type: {agent.knowledge_base?.rag_type || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                    Rules books: {getKnowledgeBaseCount(agent)}
                                  </Typography>
                                </Box>
                              }
                              maxWidth={300}
                              placement="top"
                            >
                              <Box className={styles.infoItem}>
                                <MenuBook className={styles.infoIcon} />
                                <Typography variant="caption" className={styles.infoText}>
                                  KB
                                </Typography>
                              </Box>
                            </InfoTooltip>
                          )}
                          {agent.features?.knowledge_base_d3 && (
                            <InfoTooltip
                              title="Customer Data"
                              content={
                                <Box>
                                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                    Status: {agent.features.knowledge_base_d3 ? 'Enabled' : 'Disabled'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                    RAG Type: {agent.knowledge_base_dim3?.rag_type || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                    Customer Data Sources: {getKnowledgeBaseD3Count(agent)}
                                  </Typography>
                                </Box>
                              }
                              maxWidth={300}
                              placement="top"
                            >
                              <Box className={styles.infoItem}>
                                <MenuBook className={styles.infoIcon} />
                                <Typography variant="caption" className={styles.infoText}>
                                  KB D3
                                </Typography>
                              </Box>
                            </InfoTooltip>
                          )}
                          {agent.features?.memory && (
                            <InfoTooltip
                              title="Memory"
                              content={
                                <Box>
                                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                    Status: {agent.memory?.enabled ? 'Enabled' : 'Disabled'}
                                  </Typography>
                                  {agent.memory?.enabled && (
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                      Max Messages: {agent.memory?.max_messages || 'N/A'}
                                    </Typography>
                                  )}
                                </Box>
                              }
                              maxWidth={300}
                              placement="top"
                            >
                              <Box className={styles.infoItem}>
                                <Memory className={styles.infoIcon} />
                                <Typography variant="caption" className={styles.infoText}>
                                  Memory
                                </Typography>
                              </Box>
                            </InfoTooltip>
                          )}
                          {agent.features?.context && (
                            <InfoTooltip
                              title="Context"
                              content={
                                <Box>
                                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                    Context processing is enabled for this agent
                                  </Typography>
                                </Box>
                              }
                              maxWidth={300}
                              placement="top"
                            >
                              <Box className={styles.infoItem}>
                                <Code className={styles.infoIcon} />
                                <Typography variant="caption" className={styles.infoText}>
                                  Context
                                </Typography>
                              </Box>
                            </InfoTooltip>
                          )}
                          {agent.features?.customer_data && (
                            <Tooltip title="Customer data processing enabled" arrow>
                              <Box className={styles.infoItem}>
                                <Code className={styles.infoIcon} />
                                <Typography variant="caption" className={styles.infoText}>
                                  Data
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}
                          {agent.features?.human_in_loop || agent.features?.humanInLoop ? (
                            <InfoTooltip
                              title="Human-in-Loop (HIL)"
                              content={
                                <Box>
                                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                    Human-in-Loop workflow is enabled for this agent. The agent will pause at configured steps to request human approval.
                                  </Typography>
                                </Box>
                              }
                              maxWidth={300}
                              placement="top"
                            >
                              <Box className={styles.infoItem}>
                                <Person className={styles.infoIcon} />
                                <Typography variant="caption" className={styles.infoText}>
                                  HIL
                                </Typography>
                              </Box>
                            </InfoTooltip>
                          ) : null}
                          {agent.features?.background_run || agent.features?.backgroundRun ? (
                            <InfoTooltip
                              title="Background Run"
                              content={
                                <Box>
                                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                    Background Run is enabled for this agent. The agent will execute workflows in the background without blocking the user interface.
                                  </Typography>
                                </Box>
                              }
                              maxWidth={300}
                              placement="top"
                            >
                              <Box className={styles.infoItem}>
                                <PlayCircle className={styles.infoIcon} />
                                <Typography variant="caption" className={styles.infoText}>
                                  BG Run
                                </Typography>
                              </Box>
                            </InfoTooltip>
                          ) : null}
                        </Box>

                        <Box
                          className={styles.statsPanel}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className={styles.statsGrid3}>
                            <div className={styles.statsCell}>
                              <div className={styles.statsLabel}>Tools</div>
                              <div className={styles.statsValue}>
                                {getAgentToolsCount(agent)}
                              </div>
                            </div>
                            <div className={styles.statsCell}>
                              <div className={styles.statsLabel}>Model</div>
                              <div className={styles.statsValue} title={agent.llm_model?.name || ''}>
                                {(agent.llm_model?.name || '—').length > 14
                                  ? `${(agent.llm_model?.name || '—').slice(0, 14)}…`
                                  : agent.llm_model?.name || '—'}
                              </div>
                            </div>
                            <div className={styles.statsCell}>
                              <div className={styles.statsLabel}>Memory</div>
                              <div className={styles.statsValue}>
                                {agent.memory?.enabled ? 'On' : 'Off'}
                              </div>
                            </div>
                          </div>
                        </Box>

                        <div className={styles.cardActions}>
                          <button
                            type="button"
                            className={styles.cardActionPrimary}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/agent-test?id=${agent.id}`);
                            }}
                          >
                            <Play size={12} fill="currentColor" />
                            RUN
                          </button>
                          <button
                            type="button"
                            className={styles.cardActionSecondary}
                            onClick={(e) => {
                              e.stopPropagation();
                              openAgent(agent);
                            }}
                          >
                            <ExternalLink size={12} strokeWidth={2} />
                            {agent.__shared && agent.__sharePermission !== 'write' ? 'VIEW' : 'OPEN'}
                          </button>
                        </div>
                      </Box>
                    </Box>
                  );
                };

                return (
                  <>
                    {ownFilteredAgents.length > 0 && (
                      <>
                        {sharedFilteredAgents.length > 0 && (
                          <Typography component="h2" className={styles.agentSectionHeading}>
                            My agents
                            <span className={styles.agentSectionCount}>{ownFilteredAgents.length}</span>
                          </Typography>
                        )}
                        <Box className={styles.agentsContainer}>
                          {ownFilteredAgents.map(renderAgentCard)}
                        </Box>
                      </>
                    )}
                    {sharedFilteredAgents.length > 0 && (
                      <>
                        <Typography component="h2" className={styles.agentSectionHeading}>
                          Shared with me
                          <span className={styles.agentSectionCount}>{sharedFilteredAgents.length}</span>
                        </Typography>
                        <Box className={styles.agentsContainer}>
                          {sharedFilteredAgents.map(renderAgentCard)}
                        </Box>
                      </>
                    )}
                  </>
                );
              })()}

              {/* List View */}
              {viewMode === 'list' && (
                <div className={styles.agentsTableWrap}>
                  <table className={styles.agentsTable}>
                    <thead>
                      <tr>
                        <th>Agent System</th>
                        <th>Classification</th>
                        <th>Performance</th>
                        <th>Health Status</th>
                        <th style={{ textAlign: 'right' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const renderAgentRow = (agent) => {
                        const agentStatus = getAgentStatus(agent);
                        const statusColor = getStatusColor(agentStatus);
                        const executionMode = getAgentExecutionMode(agent);

                        return (
                          <tr
                            key={agent.id}
                            onClick={() => openAgent(agent)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div className={styles.tableCellRow}>
                                <div className={styles.cardIconWrap}>
                                  <SmartToy sx={{ fontSize: 18 }} />
                                </div>
                                <div>
                                  <div className={styles.tableAgentName}>{agent.name}</div>
                                  {agent.__shared && (
                                    <Typography variant="caption" color="text.secondary">
                                      Shared by {agent.__shareOwner?.name || agent.__shareOwner?.email || 'teammate'} · {agent.__sharePermission === 'write' ? 'Can edit' : 'Read only'}
                                    </Typography>
                                  )}
                                  <div className={styles.tableAgentDesc}>
                                    {agent.description || 'No description provided'}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className={styles.tableType}>
                                {executionMode}
                              </div>
                              <div className={styles.tableCategoryLabel}>
                                {getAgentCategory(agent)}
                              </div>
                              <div className={styles.metaChips}>
                                {agent.is_publish === true && (
                                  <span className={styles.domainTag}>Published</span>
                                )}
                                {agent.variables && (
                                  <span className={styles.domainTag}>Variables</span>
                                )}
                                {getAgentToolsCount(agent) > 0 && (
                                  <span className={styles.domainTag}>
                                    {getAgentToolsCount(agent)} tools
                                  </span>
                                )}
                                {agent.features?.knowledge_base && (
                                  <span className={styles.domainTag}>KB</span>
                                )}
                                {agent.features?.knowledge_base_d3 && (
                                  <span className={styles.domainTag}>D3</span>
                                )}
                                {agent.features?.memory && (
                                  <span className={styles.domainTag}>Memory</span>
                                )}
                                {executionMode === 'Workflow' && (
                                  <span className={styles.domainTag}>Workflow</span>
                                )}
                                {executionMode === 'Team' && (
                                  <span className={styles.domainTag}>Team</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <Typography variant="body2" sx={{ fontWeight: 800, color: 'var(--text-primary)' }}>
                                {getAgentToolsCount(agent)} tools
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: 'var(--text-secondary)',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.02em',
                                  mt: 0.5,
                                }}
                              >
                                {agent.llm_model?.name || 'Model N/A'}
                              </Typography>
                            </td>
                            <td>
                              <div
                                className={`${styles.statusBadge} ${getStatusStudioTone(agentStatus)}`}
                                style={{ marginBottom: 8 }}
                              >
                                {agentStatus}
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                }}
                              >
                                <div
                                  style={{
                                    width: 64,
                                    background: 'var(--bg-hover)',
                                    borderRadius: 999,
                                    height: 6,
                                    overflow: 'hidden',
                                  }}
                                >
                                  <div
                                    style={{
                                      height: '100%',
                                      borderRadius: 999,
                                      width:
                                        String(agentStatus).toLowerCase() === 'inactive'
                                          ? '35%'
                                          : String(agentStatus).toLowerCase() === 'draft'
                                            ? '70%'
                                            : '100%',
                                      backgroundColor: statusColor,
                                    }}
                                  />
                                </div>
                              </div>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <div className={styles.tableRowActions}>
                                <Tooltip title="Test agent" arrow>
                                  <button
                                    type="button"
                                    className={`${styles.tableIconBtn} ${styles.tableIconBtnPrimary}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/agent-test?id=${agent.id}`);
                                    }}
                                  >
                                    <Play size={14} fill="currentColor" />
                                  </button>
                                </Tooltip>
                                <Tooltip title="Open agent" arrow>
                                  <button
                                    type="button"
                                    className={styles.tableIconBtn}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Routes read-only shared agents to the
                                      // playground instead of the editor.
                                      openAgent(agent);
                                    }}
                                  >
                                    <ExternalLink size={14} strokeWidth={2} />
                                  </button>
                                </Tooltip>
                                <Tooltip title="More" arrow>
                                  <button
                                    type="button"
                                    className={styles.tableIconBtn}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMenuClick(e, agent);
                                    }}
                                  >
                                    <MoreVertical size={15} strokeWidth={2} />
                                  </button>
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        );
                        };

                        return (
                          <>
                            {ownFilteredAgents.length > 0 && sharedFilteredAgents.length > 0 && (
                              <tr className={styles.tableSectionRow}>
                                <td colSpan={5}>My agents · {ownFilteredAgents.length}</td>
                              </tr>
                            )}
                            {ownFilteredAgents.map(renderAgentRow)}
                            {sharedFilteredAgents.length > 0 && (
                              <tr className={styles.tableSectionRow}>
                                <td colSpan={5}>Shared with me · {sharedFilteredAgents.length}</td>
                              </tr>
                            )}
                            {sharedFilteredAgents.map(renderAgentRow)}
                          </>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
              </>
              )}

              {/* Loading More Indicator */}
              {loadingMore && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mt: 4,
                    mb: 2,
                    gap: 2
                  }}
                >
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Loading more agents...
                  </Typography>
                </Box>
              )}

              {/* End of List Indicator */}
              {!hasMore && agents.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mt: 4,
                    mb: 2
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing all {agents.length} of {totalAgents} agents
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            elevation: 0,
            className: styles.contextMenuPaper,
          }}
        >
          <MenuItem onClick={handleTestAgent}>
            <ListItemIcon>
              <Science fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>Test Agent</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleEditAgent}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>
              {selectedAgent?.__shared && selectedAgent?.__sharePermission !== 'write'
                ? 'View Configuration'
                : 'Edit Agent'}
            </ListItemText>
          </MenuItem>
          {!selectedAgent?.__shared && (
            <MenuItem onClick={handleDuplicateAgent}>
              <ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>Duplicate</ListItemText>
            </MenuItem>
          )}
          {!selectedAgent?.__shared && canShareResources && (
            <MenuItem onClick={handleShare}>
              <ListItemIcon><Share fontSize="small" /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>Share</ListItemText>
            </MenuItem>
          )}
          {!selectedAgent?.__shared && canShareResources && (
            <MenuItem onClick={handleManageSharing}>
              <ListItemIcon><ManageAccounts fontSize="small" /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>Manage sharing</ListItemText>
            </MenuItem>
          )}
          {!selectedAgent?.__shared && (
            <MenuItem onClick={handleDeleteAgent} className={styles.menuItemDanger}>
              <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>Delete Agent</ListItemText>
            </MenuItem>
          )}
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          maxWidth="sm"
          fullWidth
          className={styles.dialog}
        >
          <DialogTitle className={styles.dialogTitle}>
            <Typography variant="h6">Delete Agent</Typography>
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <Typography variant="body1">
              Are you sure you want to delete "{agentToDelete?.name}"? This action cannot be undone.
            </Typography>
            {deletingAgent && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Deleting agent...
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <Button
              onClick={handleCancelDelete}
              className={styles.cancelButton}
              disabled={deletingAgent}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              className={styles.deleteButton}
              disabled={deletingAgent}
              startIcon={deletingAgent ? <CircularProgress size={16} /> : <Delete />}
            >
              {deletingAgent ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Duplicate Confirmation Dialog */}
        <Dialog
          open={duplicateDialogOpen}
          onClose={handleCancelDuplicate}
          maxWidth="sm"
          fullWidth
          className={styles.dialog}
        >
          <DialogTitle className={styles.dialogTitle}>
            <Typography variant="h6">Duplicate Agent</Typography>
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <Typography variant="body1">
              Are you sure you want to duplicate "{agentToDuplicate?.name}"? A new agent with the name "Copy - {agentToDuplicate?.name}" will be created.
            </Typography>
            {duplicatingAgent && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Duplicating agent...
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <Button
              onClick={handleCancelDuplicate}
              className={styles.cancelButton}
              disabled={duplicatingAgent}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDuplicate}
              variant="contained"
              className={styles.deleteButton}
              disabled={duplicatingAgent}
              startIcon={duplicatingAgent ? <CircularProgress size={16} /> : <ContentCopy />}
              sx={{
                backgroundColor: '#e36250',
                '&:hover': {
                  backgroundColor: '#d95545',
                }
              }}
            >
              {duplicatingAgent ? 'Duplicating...' : 'Duplicate'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Share Dialog */}
        <ShareAgentDialog
          open={shareDialogOpen}
          agent={agentToShare}
          onClose={handleCloseShareDialog}
          onSuccess={handleShareSuccess}
        />
        <ManageSharingDialog
          open={manageSharingOpen}
          resource={agentToManage ? { type: 'agent', id: agentToManage.id, name: agentToManage.name } : null}
          onClose={() => { setManageSharingOpen(false); setAgentToManage(null); }}
          onChanged={() => loadAgents(1, false, debouncedSearchQueryRef.current)}
        />

        {/* Share Result Snackbar */}
        <Snackbar
          open={shareSnackbarOpen}
          autoHideDuration={3000}
          onClose={handleCloseShareSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseShareSnackbar}
            severity={shareSeverity}
            sx={{
              fontFamily: '"Poppins", sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              backgroundColor: shareSeverity === 'warning'
                ? (isDarkMode ? '#4a3412' : '#fff4e5')
                : (isDarkMode ? '#1b4332' : '#e8f5e9'),
              color: isDarkMode ? '#ffffff' : (shareSeverity === 'warning' ? '#8a4b08' : '#1b5e20'),
              borderRadius: '10px',
              boxShadow: isDarkMode
                ? '0 10px 15px -3px rgba(0, 0, 0, 0.3)'
                : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              '& .MuiAlert-icon': {
                color: shareSeverity === 'warning'
                  ? (isDarkMode ? '#ffcc80' : '#ed6c02')
                  : (isDarkMode ? '#81c784' : '#2e7d32'),
              },
            }}
          >
            {shareSuccess || 'DIY Agent shared successfully'}
          </Alert>
        </Snackbar>
        </div>
      </Box>
    </ThemeProvider>
  );
};

export default Agents;
